import type { BillWordingBlock } from '../utils/billWordingHtml'
// ============================================================
// billing.types.ts — Invoicing Stage 1 (standard bill) wire types.
// Every £ here is WHOLE PENCE computed by the server
// (backend/src/lib/invoiceMath.ts); the UI only formats + sums for display.
// ============================================================
export type VatTreatment = 'NONE' | 'STANDARD' | 'AT_COST'
export type BillType = 'ON_ACCOUNT' | 'INTERIM_STATUTE' | 'FINAL_STATUTE'
// PENDING_APPROVAL: sent for approval (lines + header locked). While the firm
// requires approval (Bill.approvalRequired), a bill issues only once an ADMIN
// has APPROVED it; then anyone with case access may issue it.
export type BillStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'ISSUED' | 'PART_PAID' | 'PAID' | 'VOID'
export type CostsFormat = 'ITEMISED' | 'BY_FEE_EARNER' | 'SINGLE_LINE'
export type LineKind = 'PROFIT_COST' | 'DISBURSEMENT' | 'SUCCESS_FEE' | 'ADJUSTMENT'
export type DisbursementStatus = 'ANTICIPATED' | 'PAID' | 'BILLED'

export interface Disbursement {
  id: string
  caseId: string
  description: string
  category: string
  amountPence: number
  vatTreatment: VatTreatment
  vatPence: number
  status: DisbursementStatus
  payableTo: string
  paidAt: string | null
  invoiceId: string | null
  invoice: { id: string; number: string | null; status: BillStatus } | null
  onDraftInvoiceId: string | null
  createdAt: string
}

export interface BillTotals {
  profitCostsPence: number
  disbursementsPence: number
  vatPence: number
  profitCostsVatPence: number
  disbursementsVatPence: number
  totalPence: number
  units: number
  vatByRate: {
    standard: { netPence: number; vatPence: number }
    atCost: { netPence: number; vatPence: number }
    none: { netPence: number }
  }
}

export interface WipEntry {
  id: string
  date: string
  actionType: string
  description: string
  feeEarnerName: string
  feeEarnerId: string
  durationSeconds: number
  units: number
  // null + amountWithheld → a colleague's entry whose £ this viewer may not
  // see (server-side, lib/billingVisibility); time + units are always shown.
  ratePence: number | null
  amountPence: number | null
  vatPence: number | null
  amountWithheld?: boolean
  onDraft: boolean
}

export interface CaseBillRow {
  id: string
  number: string | null
  status: BillStatus
  billType: BillType
  createdAt: string
  issuedAt: string | null
  dueAt: string | null
  createdBy: string | null
  // amountPartial → total covers only the lines whose £ you may see ("your items");
  // an issued row's balance / credited / written off are then hidden (null).
  totalPence: number
  balancePence: number | null
  submittedAt: string | null
  creditedPence: number | null
  writtenOffPence: number | null
  amountPartial?: boolean
}

// B3b — a credit note's row in the case's bills list. amountPence null when
// its bill's credits are hidden from you (as CaseBillRow.creditedPence).
export interface CaseCreditNoteRow {
  id: string
  number: string | null
  status: 'DRAFT' | 'ISSUED'
  invoiceId: string
  billNumber: string | null
  createdAt: string
  issuedAt: string | null
  amountPence: number | null
  delivered: { at: string; method: DeliveryMethod | null } | null
}

export interface CaseBilling {
  case: { id: string; name: string; ref: string; clientId: string; matterNumber: number; accidentDate: string | null }
  wip: WipEntry[]
  disbursements: Disbursement[]
  bills: CaseBillRow[]
  // B3b — the case's credit notes, listed as their own rows (minus amounts).
  creditNotes?: CaseCreditNoteRow[]
  // onAccountHidden → a bill whose money is hidden from you drew on this case's
  // money on account, so every on-account figure is hidden (null / no sources).
  onAccountHidden?: boolean
  onAccountAvailablePence: number | null
  onAccountSources: { number: string | null; receivedAt: string; availablePence: number }[]
  // amountPartial → the £ covers only the viewer's OWN entries (+ disbursements).
  ready: BillTotals & { onAccountCreditPence: number | null; balancePence: number | null; entryCount: number; disbursementCount: number; amountPartial?: boolean }
  billedToDatePence: number
  // true → some bills in it count only "your items" (their credits hidden).
  billedToDatePartial?: boolean
  billedCount: number
}

export interface BillLine {
  id: string
  kind: LineKind
  description: string
  units: number | null
  ratePence: number | null
  // null (with amountWithheld) on a colleague's time line the viewer may not see the £ of.
  amountPence: number | null
  vatTreatment: VatTreatment
  vatPence: number | null
  lineDate: string | null
  feeEarnerName: string
  // The time entry's author (null for a non-time line / a gone entry) — for their avatar.
  feeEarnerId?: string | null
  originalUnits: number | null
  originalAmountPence: number | null
  internalNote: string
  sourceActivityId: string | null
  sourceDisbursementId: string | null
  atCostVatPence: number | null
  // Stage 2 — already credited on this line by ISSUED credit notes.
  creditedNetPence: number | null
  creditedVatPence: number | null
  amountWithheld?: boolean
  // Unissued bill only: another bill has already billed this line's source
  // (overlapping drafts) — it can't be issued here. billedOnNumber = that bill.
  billedElsewhere?: boolean
  billedOnNumber?: string | null
  // Unissued bill only: out of step with its time entry (server lineStates).
  //   'unbillable' — the entry was deleted / isn't on this case any more: struck,
  //                  left out of the totals, to be removed;
  //   'changed'    — the entry's time / rate changed since the line was drawn
  //                  (a written-down line didn't follow it): update or remove.
  // Both block submit / approve / issue. null / absent = in step.
  lineState?: 'unbillable' | 'changed' | null
}

export interface BillLetterhead {
  firm: {
    name: string
    vatNumber: string | null
    sraNumber: string | null
    address: string | null
    phone: string | null
    email: string | null
    bankAccountName: string | null
    bankSortCode: string | null
    bankAccountNumber: string | null
    complaintsContact: string | null
    // The firm logo printed top-left of the letterhead — an opaque id
    // (GET /api/firm/logos/:id → data URI; utils/firmLogo). Issued → the logo
    // frozen at issue; draft → today's. null / absent = no logo.
    logoId?: string | null
    // Document branding (utils/documentTheme, utils/documentFonts). Issued →
    // frozen at issue (absent on older bills → the default: Casevo green, no
    // secondary colour, DM Sans); draft → today's.
    accentColour?: string | null
    secondaryColour?: string | null
    headingFont?: string | null
    bodyFont?: string | null
  }
  client: { name: string; address: string[] }
  matter: { id: string; name: string; ref: string; clientId: string; matterNumber: number; accidentDate: string | null }
  signedBy: string | null
  approvedBy: string | null
  // s.69 signature block (firm billing settings; frozen at issue). signatureUrl
  // is a short-lived signed R2 URL minted by the server on every read.
  // null = no signatory recorded (pre-feature snapshot) → firm-name fallback.
  signatory: { name: string | null; title: string | null; signatureUrl: string | null } | null
  // B3 — the bill's legal wording (backend lib/billWording): frozen at issue;
  // a draft's is today's. Absent on bills issued before B3.
  wording?: BillWordingBlock | null
  // Credit notes only: the original bill + the reason (frozen at issue).
  invoice?: { id: string; number: string | null; issuedAt: string | null; totalPence: number | null }
  reason?: string
}

// ── Invoicing Stage 2 — corrections ──
export type CreditNoteMode = 'FULL' | 'AMOUNT' | 'LINES'
// netPence / vatPence null (with amountWithheld) when it credits a colleague's time line the viewer may not see the £ of.
export interface CreditNoteLine { id?: string; invoiceLineId: string; kind: LineKind; description: string; vatTreatment: VatTreatment; netPence: number | null; vatPence: number | null; amountWithheld?: boolean }
export interface CreditNote {
  id: string
  number: string | null
  status: 'DRAFT' | 'ISSUED'
  mode: CreditNoteMode
  // null (amountWithheld) when it credits a colleague's time line you may not see the £ of.
  amountPence: number | null
  netPence: number | null
  vatPence: number | null
  amountWithheld?: boolean
  reason: string
  releaseToUnbilled: boolean
  issuedAt: string | null
  createdAt: string
  createdBy: string | null
  // (Audit batch 3) Who drafted it — a draft is deleted by them, an admin or a bill approver.
  createdByUserId?: string
  lines: CreditNoteLine[]
  letterhead: BillLetterhead
  // B3b — the FIRST delivery to the client (email / post / handed over).
  delivery?: { at: string; method: DeliveryMethod | null; by: string | null } | null
}
export type DeliveryMethod = 'EMAIL' | 'POST' | 'HAND'
// A restricted viewer (no "See colleagues' billing") previewing a credit on a colleague's
// time line gets net / VAT (and, crediting chosen lines, the total) as null + amountWithheld.
export interface CreditNotePreview { amountPence: number | null; netPence: number | null; vatPence: number | null; creditablePence: number | null; lines: CreditNoteLine[]; amountWithheld?: boolean }
export interface BillPayment {
  // amountPence / allocatedPence null → hidden (Bill.moneyHidden).
  id: string; amountPence: number | null; method: string; receivedAt: string; onAccountCredit: boolean; recordedBy: string | null
  // On-account money already used against a later bill (can't be reversed).
  allocatedPence?: number | null
  // Reversed in error (admin) — stays on record, no longer counts toward Paid / Balance.
  reversed?: { at: string; by: string | null; reason: string } | null
}

export interface BillCheck { key: string; ok: boolean; block: boolean; text: string }
export interface BillHistoryItem { id: string; action: string; actor: string; details: Record<string, unknown> | null; createdAt: string }

export interface Bill {
  id: string
  // false when the viewer can see this bill only through "View all billing"
  // (no access to its case): read-only + record payments. Absent = editable.
  viewerCanEdit?: boolean
  number: string | null
  status: BillStatus
  billType: BillType
  mode: 'STANDARD' | 'PI_SETTLEMENT'
  costsFormat: CostsFormat
  narrative: string
  periodFrom: string | null
  periodTo: string | null
  issuedAt: string | null
  dueAt: string | null
  paymentTermsDays: number
  submittedAt: string | null
  createdAt: string
  updatedAt: string
  createdBy: { id: string; name: string | null }
  approvedBy: { id: string; name: string | null } | null
  approvedAt?: string | null
  // The approver's note from the last send-back (cleared on the next submit / approval).
  sendBackReason?: string | null
  case: { id: string; name: string; ref: string; clientId: string; matterNumber: number; accidentDate: string | null }
  client: { name: string; address: string[] }
  lines: BillLine[]
  totals: BillTotals
  // true → totals (and an unissued bill's on-account / balance) cover only the
  // lines whose £ this viewer may see — a colleague's are withheld.
  totalsPartial?: boolean
  // true → an ISSUED bill's money position is hidden from you (a colleague's
  // time is on it): balance, paid, credited, written off, creditable and each
  // payment / credit-note amount are null — any of them gives the total back.
  moneyHidden?: boolean
  // true → a bill whose money is hidden from you drew on this case's money on
  // account: a statute bill's on-account credit, balance, written-off figure
  // and creditable are null (each gives that draw back); on a request for
  // payment on account, each payment's allocatedPence.
  onAccountHidden?: boolean
  // Anything left to credit (yes/no — shown even when moneyHidden).
  hasCreditable?: boolean
  onAccountCreditPence: number | null
  receivedPence: number | null
  creditedPence: number | null
  balancePence: number | null
  creditablePence: number | null
  writeOff: { amountPence: number | null; at: string | null; reason: string; by: string | null } | null
  // Send bill — the FIRST delivery to the client (never moved by a resend).
  delivery: { at: string; method: 'EMAIL' | 'POST' | 'HAND' | null; by: string | null } | null
  // B3 — who signed it (set at issue; immutable after). null = not issued /
  // an on-account request / issued before B3.
  signatory?: { id: string | null; name: string } | null
  firmPaymentTermsDays: number
  payments: BillPayment[]
  creditNotes: CreditNote[]
  approvalRequired: boolean
  nextNumberPreview: string
  letterhead: BillLetterhead
  checks: BillCheck[]
  history: BillHistoryItem[]
}

export interface FirmBillRow {
  id: string
  number: string | null
  status: BillStatus
  overdue: boolean
  billType: BillType
  submittedAt: string | null
  case: { id: string; name: string; ref: string; clientId: string }
  createdBy: { id: string; name: string | null }
  // True when the bill is on a case you're involved in (owner or handling team).
  mine: boolean
  issuedAt: string | null
  dueAt: string | null
  createdAt: string
  // amountPartial → as CaseBillRow.
  totalPence: number
  balancePence: number | null
  ageDays: number | null
  creditedPence: number | null
  writtenOffPence: number | null
  amountPartial?: boolean
}

// The Activity feed's billing entries (GET /api/bill-events — a read-time
// merge of issued bills, payments received and issued credit notes; no
// Activity rows). Money is already masked server-side for a viewer who sees
// only their own £: amountPartial → the bill total covers only their items;
// amountPence null → hidden (a colleague's billing is on that bill).
export interface BillEvent {
  kind: 'invoice' | 'payment' | 'credit_note'
  id: string
  billId: string
  caseId: string
  at: string
  // Invoice / payment: the bill's number. Credit note: its own number (billNumber = the bill's).
  number: string | null
  billNumber: string | null
  billType: BillType
  status: BillStatus
  method: string | null
  amountPence: number | null
  amountPartial: boolean
  actor: { id: string; name: string | null } | null
}

// A client statement (GET /api/clients/:clientId/statement — on demand, writes
// nothing): issued bills of costs for one client, each row ALREADY masked for
// this viewer. totals are null (withheld, partial: true) if ANY row is partial
// or hidden — never a sum that could back out a hidden figure.
export interface ClientStatementRow {
  id: string
  number: string | null
  status: BillStatus
  billType: BillType
  issuedAt: string | null
  dueAt: string | null
  case: { id: string; name: string; ref: string }
  totalPence: number
  amountPartial: boolean
  paidPence: number | null
  creditsPence: number | null
  balancePence: number | null
}
export interface ClientStatement {
  client: { id: string; name: string; address: string[] }
  firm: {
    name: string; vatNumber: string | null; sraNumber: string | null; address: string | null; phone: string | null; email: string | null
    bankAccountName: string | null; bankSortCode: string | null; bankAccountNumber: string | null; complaintsContact: string | null
    logoId?: string | null
    // (Audit fix 1) Today's bill wording — complaints + SRA notices (absent
    // from an older server → the old text).
    wording?: BillWordingBlock | null
    // Today's document branding (null = the default).
    accentColour?: string | null
    secondaryColour?: string | null
    headingFont?: string | null
    bodyFont?: string | null
  }
  asAt: string
  bills: ClientStatementRow[]
  totals: { totalPence: number | null; paidPence: number | null; creditsPence: number | null; balancePence: number | null; partial: boolean }
}

// B3c — a credit note's row on the firm Bills page ("Credit notes" tab).
// amountPence null when its bill's credits are hidden from you.
export interface FirmCreditNoteRow {
  id: string
  number: string | null
  status: 'DRAFT' | 'ISSUED'
  invoiceId: string
  billNumber: string | null
  case: { id: string; name: string; ref: string }
  createdBy: { id: string; name: string | null }
  mine: boolean
  issuedAt: string | null
  createdAt: string
  amountPence: number | null
  delivered: { at: string; method: DeliveryMethod | null } | null
}
export interface FirmBills {
  bills: FirmBillRow[]
  creditNotes?: FirmCreditNoteRow[]
  kpis: {
    unbilledWipPence: number
    unbilledWipUnits: number
    unbilledWipCases: number
    // *Partial → that £ covers only what you may see ("your items").
    unbilledWipPartial?: boolean
    draftsPence: number
    draftsCount: number
    draftsPartial?: boolean
    awaitingApprovalCount: number
    unpaidPence: number
    unpaidCount: number
    unpaidPartial?: boolean
    unpaidAvgAgeDays: number
    over60Pence: number
    over60Count: number
    over60Partial?: boolean
    aged: { d0_30: number; d31_60: number; d61_90: number; d90p: number }
  }
}

export const DISBURSEMENT_CATEGORIES: { key: string; label: string; vat: VatTreatment }[] = [
  { key: 'court_fee', label: 'Court fee', vat: 'NONE' },
  { key: 'counsel', label: 'Counsel', vat: 'AT_COST' },
  { key: 'medical', label: 'Medical', vat: 'STANDARD' },
  { key: 'expert', label: 'Expert', vat: 'STANDARD' },
  { key: 'police', label: 'Police', vat: 'NONE' },
  { key: 'travel', label: 'Travel', vat: 'STANDARD' },
  { key: 'other', label: 'Other', vat: 'STANDARD' },
]
export const categoryLabel = (k: string): string => DISBURSEMENT_CATEGORIES.find((c) => c.key === k)?.label ?? 'Other'

// ── B3 — Settings → Bill wording (admins) ─────────────────────────────
export type LegalStructure = 'LIMITED_COMPANY' | 'LLP' | 'PARTNERSHIP' | 'SOLE_PRACTITIONER'
export interface BillWordingSettings {
  legalName: string | null
  companyRegistration: string | null
  // B3b — the firm's legal structure (null = not set). A partnership / sole
  // practitioner needs no company registration (companyRegistrationRequired).
  legalStructure: LegalStructure | null
  legalStructures: { key: LegalStructure; label: string }[]
  companyRegistrationRequired: boolean
  sraNumber: string | null
  vatNumber: string | null
  firmName: string
  complaintsContact: string | null
  complaintsContactName: string | null
  complaintsContactRole: string | null
  complaintsEmail: string | null
  complaintsPhone: string | null
  complaintsProcedureUrl: string | null
  billRightsWording: string | null
  billComplaintsWording: string | null
  billSraFooter: string | null
  defaults: { billRightsWording: string; billComplaintsWording: string; billSraFooter: string }
  placeholders: { key: string; label: string }[]
  legalOmbudsman: string
  preview: { rights: string; complaints: string; sraFooter: string }
  // (Audit fix 1) Sent back on approve — the text the approver saw.
  previewHash: string
  problems: string[]
  approved: boolean
  approvedAt: string | null
  approvedBy: string | null
}
export type BillWordingPatch = Partial<Pick<BillWordingSettings,
  'legalName' | 'companyRegistration' | 'legalStructure' | 'complaintsContactName' | 'complaintsContactRole' | 'complaintsEmail' | 'complaintsPhone' | 'complaintsProcedureUrl'
  | 'billRightsWording' | 'billComplaintsWording' | 'billSraFooter'>>
// hasSignature (B3b) — their own signature image will print above the typed line.
export interface BillSignatoryOption { id: string; name: string; jobTitle: string | null; isFeeEarner: boolean; hasSignature?: boolean }
// B3b — a user's own signature image (My settings / Admin › Team).
export interface UserSignature { hasSignature: boolean; signatureUrl: string | null; updatedAt: string | null }
// B3b — the old firm-level signature, until an admin moves it to its owner.
export interface FirmSignature { present: boolean; signatureUrl: string | null; name: string | null; title: string | null }
export interface BillSignatories { required: boolean; options: BillSignatoryOption[]; defaultId: string | null }
